/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import CopyToClipboard from "react-copy-to-clipboard";
import { When } from "react-if";
import { generatePath, Link } from "react-router-dom";
import { FlexContainer, SVGIcon, Tag, Text, theme } from "@nordcloud/gnui";
import { Result, ResultLabel, WasteIndicator } from "~/components";
import {
  DEFAULT_SAVING_SUGGESTION,
  estateCategories,
  SUCCESS_TEXT,
} from "~/constants";
import { ROUTES } from "~/routing/routes";
import { showSimpleInfo } from "~/services/toast";
import { isCloudResource, trimText } from "~/tools";
import { SearchResources } from "./types";

type Color = React.ComponentProps<typeof Tag>["color"];

type Props = {
  result: SearchResources;
  onSetShowResults: (state: boolean) => void;
};

export function ResultItem({ result, onSetShowResults }: Props) {
  const categoryResults = estateCategories[
    result.__typename ?? "CloudResource"
  ] ?? {
    label: result.__typename,
  };

  const { id, providerType } = result;

  const { showWasteIndicator, wasteReason, amount } = getCloudWasteData(result);

  const visibleNameSize = showWasteIndicator ? 15 : 25;
  const visibleName = isCloudResource(result)
    ? trimText(result.name, visibleNameSize)
    : providerType;

  return (
    <Result direction="column" alignItems="flex-start">
      <FlexContainer css={{ width: "100%" }}>
        <ResultLabel>Name:</ResultLabel>
        <FlexContainer justifyContent="space-between" css={{ width: "100%" }}>
          <Text mb={0}>{visibleName}</Text>
          <When condition={showWasteIndicator}>
            <Link to={generatePath(ROUTES.resource, { nordcloudId: id })}>
              <WasteIndicator
                reason={wasteReason}
                amount={{
                  amount: amount ?? "0",
                  uri: undefined,
                }}
              />
            </Link>
          </When>
          <Tag
            text={categoryResults.label}
            /*
             TODO: extend interface in `GNUI` so those colors are fully compatible in types
                   currently we only assume it without hard type checking
             */
            color={categoryResults.color as Color}
            css={{ whiteSpace: "nowrap" }}
          />
        </FlexContainer>
      </FlexContainer>
      <FlexContainer css={{ width: "100%" }}>
        <ResultLabel>IBM MCA Id:</ResultLabel>
        <FlexContainer css={{ width: "100%" }}>
          <Link
            to={generatePath(ROUTES.resource, { nordcloudId: id })}
            onClick={() => onSetShowResults(false)}
          >
            {id}
          </Link>
          <CopyToClipboard
            text={id ?? ""}
            onCopy={() => showSimpleInfo(SUCCESS_TEXT.copied)}
          >
            <SVGIcon
              name="copy"
              size="sm"
              css={{
                cursor: "pointer",
                marginLeft: theme.spacing.spacing02,
              }}
            />
          </CopyToClipboard>
        </FlexContainer>
      </FlexContainer>
    </Result>
  );
}

function getCloudWasteData(result: SearchResources) {
  if (result != null && isCloudResource(result)) {
    const isWaste = result?.cloudWaste?.isWaste ?? false;
    const reason = result?.cloudWaste?.reason ?? "unknown";
    const wasteReason = isWaste ? reason : DEFAULT_SAVING_SUGGESTION;
    const savingsAmountCount = result?.savingsAmount?.count ?? 0;
    const amount = result?.savingsAmount?.amount ?? "0";
    const hasSavingRecommendations = parseFloat(amount) > 0;

    const showWasteIndicator = hasSavingRecommendations && isWaste;
    const tooltipReason =
      savingsAmountCount > 1
        ? `${savingsAmountCount} savings suggestions found`
        : wasteReason;

    return {
      wasteReason: tooltipReason ?? "unknown",
      showWasteIndicator,
      amount,
    };
  }

  return {};
}
